import { CalendarClock, Pill, ShieldAlert } from 'lucide-react';
import { tr } from '@/lib/i18n-core';
import { FormSection } from '@/components/ui/form-screen';
import { Field, Input, Select, StatusBadge, Switch } from '@/components/ui/primitives';
import { DrugSearchInput } from '@/features/opd/DrugSearchInput';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { InstantPicker } from './controls';
import type { FieldsProps } from './form-props';
import { COMMON_ROUTES, DOSE_UNITS, OTHER_ROUTES, allergyMatches, ipdRouteOf, type MedicationDraft, type MedicationEnd } from './order-form';
import { FrequencyOrInterval, StopAtField } from './ScheduleFields';

const routeLabel = (route: string) => tr('ipd', `io_form_route_${route}`);

export function MedicationFields({ draft, update, error, common, setCommon, admission }: FieldsProps<MedicationDraft>) {
  const matches = allergyMatches(draft.drugName, admission.patient.allergies);
  const unitIsChip = (DOSE_UNITS as readonly string[]).includes(draft.doseUnit);
  const otherRoute = (OTHER_ROUTES as readonly string[]).includes(draft.route) ? draft.route : '';
  const highAlert = draft.isHighAlert || draft.controlled;
  const endOptions: { value: MedicationEnd; label: string }[] = [
    { value: 'open', label: tr('ipd', 'io_form_end_open') },
    { value: 'days', label: tr('ipd', 'io_form_end_days') },
    { value: 'time', label: tr('ipd', 'io_form_end_time') },
  ];

  return (
    <>
      <FormSection title={tr('ipd', 'io_form_section_medicine')} icon={Pill}>
        <Field
          label={tr('ipd', 'io_form_drug')}
          hint={tr('ipd', 'io_form_drug_hint')}
          required
          error={error('drugName') ?? (matches.length > 0 ? tr('ipd', 'io_form_allergy_match', { substance: matches.join(', ') }) : undefined)}
          className="sm:col-span-2"
        >
          <DrugSearchInput
            value={draft.drugName}
            onType={(text) => update({ drugName: text, productId: null, controlled: false })}
            onChoose={(choice) => update({ drugName: choice.drugName, productId: choice.productId, controlled: Boolean(choice.isControlled), route: ipdRouteOf(choice.route) ?? draft.route })}
          />
        </Field>
        {highAlert && (
          <div className="flex flex-wrap items-center gap-2 sm:col-span-2">
            <StatusBadge status="high_alert" tone="destructive" label={tr('ipd', 'io_form_high_alert_badge')} />
            <span className="text-xs text-muted-foreground">{tr('ipd', draft.controlled ? 'io_form_controlled' : 'io_form_high_alert_hint')}</span>
          </div>
        )}
        <Field label={tr('ipd', 'io_form_dose')} required error={error('dose')}>
          <Input type="number" inputMode="decimal" min={0} step="any" value={draft.dose} onChange={(event) => update({ dose: event.target.value })} />
        </Field>
        <Field label={tr('ipd', 'io_form_dose_unit')} required error={error('doseUnit')}>
          <div className="space-y-2">
            <ChoiceButtons layout="chips"
              aria-label={tr('ipd', 'io_form_dose_unit')}
              value={unitIsChip ? draft.doseUnit : ''}
              options={DOSE_UNITS.map((unit) => ({ value: unit, label: unit }))}
              onChange={(doseUnit) => update({ doseUnit })}
            />
            <Input
              aria-label={tr('ipd', 'io_form_unit_other')}
              placeholder={tr('ipd', 'io_form_unit_other')}
              maxLength={20}
              value={unitIsChip ? '' : draft.doseUnit}
              onChange={(event) => update({ doseUnit: event.target.value })}
            />
          </div>
        </Field>
        <Field label={tr('ipd', 'io_form_route')} required error={error('route')} className="sm:col-span-2">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
            <ChoiceButtons layout="chips"
              aria-label={tr('ipd', 'io_form_route')}
              className="sm:flex-1"
              value={(COMMON_ROUTES as readonly string[]).includes(draft.route) ? draft.route : ''}
              options={COMMON_ROUTES.map((route) => ({ value: route, label: routeLabel(route) }))}
              onChange={(route) => update({ route })}
            />
            <Select
              aria-label={tr('ipd', 'io_form_route_more')}
              className="sm:w-56"
              placeholder={tr('ipd', 'io_form_route_more')}
              value={otherRoute}
              onChange={(event) => update({ route: event.target.value as MedicationDraft['route'] })}
            >
              {OTHER_ROUTES.map((route) => (
                <option key={route} value={route}>
                  {routeLabel(route)}
                </option>
              ))}
            </Select>
          </div>
        </Field>
        <div className="sm:col-span-2">
          <Switch
            checked={highAlert}
            disabled={draft.controlled}
            onCheckedChange={(isHighAlert) => update({ isHighAlert })}
            label={
              <span className="inline-flex items-center gap-1.5">
                <ShieldAlert className="h-4 w-4 text-destructive" aria-hidden />
                {tr('ipd', 'io_form_high_alert')}
              </span>
            }
            description={tr('ipd', 'io_form_high_alert_hint')}
          />
        </div>
      </FormSection>

      <FormSection title={tr('ipd', 'io_form_section_when')} icon={CalendarClock}>
        <div className="sm:col-span-2">
          <Switch checked={draft.prn} onCheckedChange={(prn) => update({ prn })} label={tr('ipd', 'io_form_prn')} description={tr('ipd', 'io_form_prn_hint')} />
        </div>
        {draft.prn ? (
          <>
            <Field label={tr('ipd', 'io_form_prn_indication')} required error={error('prnIndication', 'frequency')}>
              <Input maxLength={240} placeholder={tr('ipd', 'io_form_prn_indication_placeholder')} value={draft.prnIndication} onChange={(event) => update({ prnIndication: event.target.value })} />
            </Field>
            <Field label={tr('ipd', 'io_form_prn_max')} error={error('prnMaxDosesPerDay')}>
              <Input type="number" inputMode="numeric" min={1} max={24} value={draft.prnMaxDosesPerDay} onChange={(event) => update({ prnMaxDosesPerDay: event.target.value })} />
            </Field>
          </>
        ) : (
          <>
            <FrequencyOrInterval draft={draft} update={update} error={error} required />
            <Field label={tr('ipd', 'io_form_first_dose_at')} hint={tr('ipd', 'io_form_first_dose_hint')} hintBelow error={error('firstDoseAt')}>
              <InstantPicker value={draft.firstDoseAt} onChange={(firstDoseAt) => update({ firstDoseAt })} />
            </Field>
          </>
        )}
        <Field label={tr('ipd', 'io_form_how_long')} className="sm:col-span-2">
          <ChoiceButtons layout="chips" value={draft.endMode} options={endOptions} onChange={(endMode) => update({ endMode })} />
        </Field>
        {draft.endMode === 'days' && (
          <Field label={tr('ipd', 'io_form_duration_days')} required error={error('durationDays')}>
            <Input type="number" inputMode="numeric" min={1} max={365} value={draft.durationDays} onChange={(event) => update({ durationDays: event.target.value })} />
          </Field>
        )}
        {draft.endMode === 'time' && <StopAtField common={common} setCommon={setCommon} error={error} hint={tr('ipd', 'io_form_last_dose_hint')} />}
      </FormSection>
    </>
  );
}
